import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { Button, Modal, message as antMessage } from "antd";
import { motion } from "framer-motion";
import { formatDistanceToNow } from "date-fns";
import {
  MessageOutlined,
  FormOutlined,
  ThunderboltFilled,
} from "@ant-design/icons";
import agentService from "../../../api/agent/agentService";
import { askAiTokens as t } from "./components/askai/tokens";
import { Attachment, SidePanelKind } from "./components/askai/types";
import {
  nextAttachmentId,
  validateFile,
} from "./components/askai/fileUtils";
import {
  useAskAiSession,
  ASK_AI_THINKING_STEPS,
} from "./components/askai/useAskAiSession";
import ChatHeader from "./components/askai/ChatHeader";
import EmptyState from "./components/askai/EmptyState";
import MessageList from "./components/askai/MessageList";
import ChatInput from "./components/askai/ChatInput";
import DropZoneOverlay from "./components/askai/DropZoneOverlay";
import SidePanel from "./components/askai/SidePanel";
import DocumentsPanel from "./components/askai/DocumentsPanel";
import HistoryPanel from "./components/askai/HistoryPanel";
import { DocViewerProvider } from "./components/askai/DocViewer";
import { useIsMobile } from "./components/askai/useMediaQuery";

interface Props {
  tenderID?: string;
  tenderData?: any;
  // When true the surface escapes its tab container and covers the full
  // viewport. Used so the chat isn't squeezed by the app sidebar, top bar,
  // and the BAFO Analysis tab strip.
  fullScreen?: boolean;
  onExit?: () => void;
}

const AskAIView: React.FC<Props> = ({
  tenderID,
  tenderData,
  fullScreen = false,
  onExit,
}) => {
  const tenderRef: string | number | undefined =
    tenderID || tenderData?.tenderID;

  const {
    messages,
    sessionId,
    sessionLoading,
    isStreaming,
    thinkingLabel,
    thinkingStep,
    restoredSession,
    send,
    regenerateLast,
    cancelStreaming,
    resetSession,
    loadSession,
  } = useAskAiSession({ tenderRef });

  const isMobile = useIsMobile();
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  // Desktop: sidebar stays open by default with the chat history visible.
  // Mobile: closed by default; opens as a slide-over drawer via the header
  // toggle. The X / backdrop tap closes it.
  const [sidePanel, setSidePanel] = useState<SidePanelKind>(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
      ? null
      : "history"
  );
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [feedback, setFeedback] = useState<Record<number, "good" | "bad">>({});
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);

  // Side-panel data — lazy-loaded the first time the panel opens.
  const [docs, setDocs] = useState<any[] | null>(null);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<any[] | null>(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState<string | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);
  const copyTimerRef = useRef<number | null>(null);

  // Resume / start-new decision: when a prior session is restored on mount,
  // we hold rendering of the chat until the user confirms. Reset on tender
  // change so a new tender shows its own prompt (if any).
  const [resumeDecided, setResumeDecided] = useState(false);
  useEffect(() => {
    setResumeDecided(false);
  }, [tenderRef]);

  // Resolve the displayed thinking label — when the streamer hasn't set an
  // explicit tool label, fall back to the rotating step text from the
  // hook's canned list (procurement-flavoured static rotation).
  const displayThinkingLabel =
    thinkingLabel === "Thinking"
      ? ASK_AI_THINKING_STEPS[thinkingStep % ASK_AI_THINKING_STEPS.length]
      : thinkingLabel;

  // Invalidate cached panel data whenever the tender changes.
  useEffect(() => {
    setDocs(null);
    setSessions(null);
    setDocsError(null);
    setSessionsError(null);
  }, [tenderRef]);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
    };
  }, []);

  const fetchDocs = useCallback(async () => {
    if (!tenderRef) return;
    setDocsLoading(true);
    setDocsError(null);
    try {
      const res: any = await agentService.listDocs({ tender_id: tenderRef });
      const items: any[] = Array.isArray(res?.docs) ? res.docs : [];
      setDocs(items);
    } catch (err: any) {
      console.error("Failed to load docs", err);
      setDocsError(err?.message || "Failed to load documents");
      setDocs([]);
    } finally {
      setDocsLoading(false);
    }
  }, [tenderRef]);

  const fetchSessions = useCallback(async () => {
    if (!tenderRef) return;
    setSessionsLoading(true);
    setSessionsError(null);
    try {
      const res: any = await agentService.listChatSessions({
        tender_id: tenderRef,
        limit: 50,
      });
      const items: any[] = Array.isArray(res?.sessions) ? res.sessions : [];
      setSessions(items);
    } catch (err: any) {
      console.error("Failed to load chat sessions", err);
      setSessionsError(err?.message || "Failed to load chat history");
      setSessions([]);
    } finally {
      setSessionsLoading(false);
    }
  }, [tenderRef]);

  const togglePanel = useCallback(
    (panel: Exclude<SidePanelKind, null>) => {
      setSidePanel((prev) => {
        // Mobile: tapping the active toggle closes the drawer; tapping the
        // other one switches. Desktop: tapping the active toggle is a no-op
        // (the panel can't be closed — drawer stays anchored as a side rail).
        const next =
          prev === panel ? (isMobile ? null : prev) : panel;
        if (next === "documents" && docs === null) fetchDocs();
        if (next === "history" && sessions === null) fetchSessions();
        return next;
      });
    },
    [docs, fetchDocs, fetchSessions, sessions, isMobile]
  );

  // Pre-fetch data for the currently-open panel — fires on first mount when
  // the tender id is ready, and whenever a consumer (e.g. handleNewChat)
  // invalidates the cache by setting docs/sessions back to null.
  useEffect(() => {
    if (!tenderRef) return;
    if (sidePanel === "history" && sessions === null) fetchSessions();
    else if (sidePanel === "documents" && docs === null) fetchDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenderRef, sidePanel, sessions, docs]);

  // When a brand new session is created (sessionId flips from null → value
  // after the user's first send in a fresh chat), invalidate the history
  // cache so the sidebar reflects the new conversation immediately.
  const prevSessionIdRef = useRef<string | null>(null);
  useEffect(() => {
    const prev = prevSessionIdRef.current;
    prevSessionIdRef.current = sessionId;
    // Only refresh when a session was just minted in this view's lifetime —
    // skip the initial hydration where prev is null on first mount.
    if (prev === null && sessionId && sessions !== null) {
      setSessions(null);
    }
  }, [sessionId, sessions]);

  const addAttachments = useCallback((files: FileList | File[]) => {
    const list = Array.from(files);
    if (list.length === 0) return;
    const accepted: Attachment[] = [];
    list.forEach((f) => {
      const reason = validateFile(f);
      if (reason) {
        antMessage.warning(reason);
        return;
      }
      accepted.push({
        id: nextAttachmentId(),
        file: f,
        status: "queued",
        progress: 0,
      });
    });
    if (accepted.length > 0) {
      setAttachments((prev) => [...prev, ...accepted]);
    }
  }, []);

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const markAttachmentReady = useCallback((filename: string) => {
    setAttachments((prev) =>
      prev.map((a) =>
        a.file.name === filename && a.status !== "ready"
          ? { ...a, status: "ready", progress: 100 }
          : a
      )
    );
  }, []);

  const handleSend = useCallback(async () => {
    const text = input;
    const sending = attachments.slice();
    // Optimistically mark them as uploading so the chips animate while the
    // request is in flight.
    setAttachments((prev) =>
      prev.map((a) =>
        sending.find((s) => s.id === a.id)
          ? { ...a, status: "uploading", progress: 10 }
          : a
      )
    );
    setInput("");
    await send({
      text,
      attachments: sending,
      onUploadFinished: markAttachmentReady,
    });
    // Once streaming has fully ended, clear the chips so the composer is
    // fresh for the next turn.
    setAttachments([]);
  }, [attachments, input, markAttachmentReady, send]);

  const handleStop = useCallback(() => {
    cancelStreaming();
  }, [cancelStreaming]);

  const handleNewChat = useCallback(() => {
    resetSession();
    setAttachments([]);
    setInput("");
    setFeedback({});
    setCopiedIdx(null);
    // Force the History panel to reload so the freshly created session shows
    // up next time the user opens it.
    setSessions(null);
    antMessage.success("Started a new chat");
  }, [resetSession]);

  // Resolve the tender label used in share/copy titles and the empty-state
  // hero. Prefer the human-friendly tender number when available.
  const tenderTitle =
    tenderData?.tenderNumber ||
    tenderID ||
    tenderData?.tenderID ||
    "this tender";

  // Build the clipboard / share payload — strip every URL and citation so
  // the receiver (Notes, Slack, email) gets clean attribution-prefixed prose
  // with markdown formatting (headings, bold, lists, tables) preserved.
  const formatForShare = useCallback(
    (rawText: string): { title: string; body: string; combined: string } => {
      const title = `ProcureX IntelliSuite - Tender - ${tenderTitle}`;
      let clean = rawText || "";

      // 1) Pre-rendered anchor source citations: *<a …>(Source: foo)</a>*
      clean = clean.replace(
        /\s*\*?\s*<a\b[^>]*>\s*\(\s*Sources?:[^<]*\)\s*<\/a>\s*\*?/gi,
        ""
      );
      // 2) Plain-text source citations: *(Source: foo)* / (Source: foo, sheet "X")
      clean = clean.replace(
        /\s*\*?\s*\(\s*Sources?:[^)]*\)\s*\*?/gi,
        ""
      );
      // 3) Superscript / subscript citation markers (e.g. `<sup>📎</sup>`,
      //    `<sup>1</sup>`) — strip the entire block including content since
      //    they only make sense rendered, not as bare text.
      clean = clean.replace(/<sup\b[^>]*>[\s\S]*?<\/sup>/gi, "");
      clean = clean.replace(/<sub\b[^>]*>[\s\S]*?<\/sub>/gi, "");
      // 4) Any remaining <a> tags — keep inner text, drop href/url wrapper.
      clean = clean.replace(/<a\b[^>]*>([\s\S]*?)<\/a>/gi, "$1");
      // 5) Strip any other stray HTML tags (span, code, br, hr, etc.) —
      //    keep their inner text content.
      clean = clean.replace(/<\/?[a-zA-Z][^>]*>/g, "");
      // 6) Markdown image syntax ![alt](url) — keep just the alt text.
      clean = clean.replace(/!\[([^\]]*)\]\([^)]*\)/g, "$1");
      // 7) Markdown link syntax [text](url) — keep just the text.
      clean = clean.replace(/\[([^\]]+)\]\([^)]*\)/g, "$1");
      // 8) Bare http/https URLs anywhere in the prose — drop them.
      clean = clean.replace(/https?:\/\/[^\s)<>\]]+/gi, "");
      // 9) Trailing "Sources:" / "References:" / "Citations:" section + bullets.
      const SOURCES_LINE_RE =
        /^\s*(?:#{1,6}\s+)?(?:\*\*\s*)?(?:sources?|references?|citations?)(?:\s*\*\*)?\s*:?\s*$/i;
      const lines = clean.split("\n");
      for (let i = 0; i < lines.length; i++) {
        if (SOURCES_LINE_RE.test(lines[i])) {
          clean = lines.slice(0, i).join("\n").trimEnd();
          break;
        }
      }
      // 10) Strip markdown syntax — the receiver (Notes, Slack body, plain
      //     email, etc.) doesn't render markdown, so leaving `##`, `**`,
      //     backticks etc. in the text shows them literally. Lists keep
      //     their `-` / `*` markers because those still read as bullets.
      clean = clean
        // Fenced code blocks ```lang\n…\n``` → just the inner code
        .replace(/```[\w-]*\n?([\s\S]*?)\n?```/g, "$1")
        // Inline code `foo` → foo
        .replace(/`([^`\n]+)`/g, "$1")
        // Headings: # / ## / ### / etc. → strip the leading hashes
        .replace(/^#{1,6}\s+/gm, "")
        // Bold: **foo** and __foo__ → foo
        .replace(/\*\*([^*\n]+?)\*\*/g, "$1")
        .replace(/__([^_\n]+?)__/g, "$1")
        // Strikethrough: ~~foo~~ → foo
        .replace(/~~([^~\n]+?)~~/g, "$1")
        // Blockquote prefix "> foo" → "foo"
        .replace(/^>\s?/gm, "")
        // Horizontal rules (---, ***, ___) → drop the line
        .replace(/^[ \t]*[-*_]{3,}[ \t]*$/gm, "");
      // 11) Whitespace cleanup: collapse runs of spaces left by URL strips,
      //     strip trailing whitespace on each line, and collapse 3+ blank
      //     lines down to a single blank line.
      clean = clean
        .split("\n")
        .map((l) => l.replace(/[ \t]+/g, " ").replace(/ +$/g, ""))
        .join("\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim();

      return {
        title,
        body: clean,
        combined: clean ? `${title}\n\n${clean}` : title,
      };
    },
    [tenderTitle]
  );

  const handleCopy = useCallback(
    async (text: string, idx: number) => {
      const { combined } = formatForShare(text);
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(combined);
        } else {
          const ta = document.createElement("textarea");
          ta.value = combined;
          ta.style.position = "fixed";
          ta.style.left = "-9999px";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }
        setCopiedIdx(idx);
        antMessage.success("Copied to clipboard");
        if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current);
        copyTimerRef.current = window.setTimeout(
          () => setCopiedIdx(null),
          1600
        );
      } catch {
        antMessage.error("Unable to copy");
      }
    },
    [formatForShare]
  );

  // POSTs the rating to /chat/messages/:messageId/feedback. The endpoint is
  // idempotent — re-POSTing the opposite rating toggles, so we just send and
  // mirror the result in local state.
  const postFeedback = useCallback(
    async (idx: number, rating: "good" | "bad", reason?: string) => {
      const msg = messages[idx];
      const messageId = msg?.messageId;
      if (!messageId) {
        antMessage.warning(
          "Feedback isn't available for this message yet — try again once it finishes streaming."
        );
        return;
      }
      try {
        setFeedbackSubmitting(true);
        await agentService.submitMessageFeedback(messageId, {
          rating,
          ...(reason && reason.trim() ? { reason: reason.trim() } : {}),
        });
        setFeedback((prev) => ({ ...prev, [idx]: rating }));
        antMessage.success(
          rating === "good"
            ? "Thanks for the feedback!"
            : "Thanks — we'll use this to improve responses."
        );
      } catch (err: any) {
        console.error("Feedback submit failed", err);
        antMessage.error(err?.message || "Unable to submit feedback");
      } finally {
        setFeedbackSubmitting(false);
      }
    },
    [messages]
  );

  const handleFeedback = useCallback(
    (idx: number, value: "good" | "bad") => {
      // Toggle-off: clicking the same rating again clears the local
      // highlight. The backend has no DELETE — the last submitted rating
      // stays recorded server-side.
      if (feedback[idx] === value) {
        setFeedback((prev) => {
          const next = { ...prev };
          delete next[idx];
          return next;
        });
        return;
      }
      // Both 👍 and 👎 POST immediately with the rating only — no reason
      // captured for now.
      void postFeedback(idx, value);
    },
    [feedback, postFeedback]
  );

  const handleShare = useCallback(
    async (text: string) => {
      const { title, body, combined } = formatForShare(text);
      // Native share sheet first — apps like Notes pick up `title`
      // separately from `text`, so we pass them split.
      try {
        if (typeof navigator.share === "function") {
          await navigator.share({ title, text: body });
          return;
        }
      } catch {
        // user cancelled or share failed — fall through to clipboard
      }
      try {
        if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(combined);
          antMessage.success("Response copied — paste it anywhere to share");
        } else {
          antMessage.info("Sharing isn't supported on this browser");
        }
      } catch {
        antMessage.error("Unable to share");
      }
    },
    [formatForShare]
  );

  const handleDownloadImage = useCallback(
    (url: string, name?: string) => {
      const a = document.createElement("a");
      a.href = url;
      a.download = name || `ai-response-${Date.now()}.png`;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    []
  );

  const handleRegenerate = useCallback(() => {
    regenerateLast({ attachments: [] });
  }, [regenerateLast]);

  const handleSelectSession = useCallback(
    async (sess: { session_id: string; title?: string }) => {
      try {
        const count = await loadSession(sess.session_id);
        antMessage.success(
          `Loaded ${sess.title || "conversation"} (${count} message${
            count === 1 ? "" : "s"
          })`
        );
      } catch {
        // Error already toasted inside the hook.
      }
    },
    [loadSession]
  );

  // Drag-and-drop handlers — only counts drags that contain files so the
  // overlay doesn't flicker when users drag text selections.
  const dragHasFiles = (e: React.DragEvent<HTMLDivElement>) =>
    Array.from(e.dataTransfer?.types || []).includes("Files");

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (!dragHasFiles(e)) return;
    dragCounterRef.current += 1;
    if (dragCounterRef.current === 1) setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (!dragHasFiles(e)) return;
    dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
    if (dragCounterRef.current === 0) setIsDragging(false);
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (!dragHasFiles(e)) return;
    e.preventDefault();
    dragCounterRef.current = 0;
    setIsDragging(false);
    if (e.dataTransfer?.files?.length) {
      addAttachments(e.dataTransfer.files);
    }
  };

  // Pre-compute the persisted AttachmentMeta count for the most recent user
  // message — used purely for layout decisions.
  const hasMessages = messages.some((m) => m.role === "user");
  const awaitingResumeDecision =
    !sessionLoading && !resumeDecided && restoredSession !== null;

  const handleResume = useCallback(() => {
    setResumeDecided(true);
  }, []);

  const handleStartNew = useCallback(() => {
    resetSession();
    setAttachments([]);
    setInput("");
    setFeedback({});
    setCopiedIdx(null);
    setSessions(null);
    setResumeDecided(true);
  }, [resetSession]);

  const wrapperStyle: React.CSSProperties = fullScreen
    ? {
        position: "fixed",
        inset: 0,
        // App's fixed topbar (.nk-header-fixed) is z-index 1021 — overlay
        // has to sit above it so the chat header isn't clipped. Stays below
        // antd tooltips/popovers (~1070) so our own button tooltips still
        // appear above the chat.
        zIndex: 1050,
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        background: t.color.surface,
      }
    : {
        display: "flex",
        flexDirection: "column",
        height: "calc(100vh - 10px)",
        minHeight: 420,
        maxHeight: "calc(100vh - 200px)",
        overflow: "hidden",
        background: t.color.surface,
      };

  return (
    <DocViewerProvider>
    <div style={wrapperStyle}>
      <style>{`
        @property --angle {
          syntax: '<angle>';
          initial-value: 0deg;
          inherits: false;
        }
        @keyframes askai-progress-slide {
          0%   { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .askai-progress-indeterminate {
          width: 40%;
          height: 100%;
          background: linear-gradient(90deg, transparent, ${t.color.accent}, transparent);
          animation: askai-progress-slide 1.1s ease-in-out infinite;
        }
        @keyframes askai-angle-spin {
          to { --angle: 360deg; }
        }
        .askai-composer { --angle: 0deg; }
        .askai-composer-focused {
          animation: askai-angle-spin 6s linear infinite;
        }
        .askai-input-shell textarea::placeholder {
          color: ${t.color.textSubtle};
        }
        .askai-message-actions .ant-btn-text:hover {
          background: ${t.color.surfaceMuted} !important;
        }
        .askai-kbd {
          font-family: ${t.font.mono};
          font-size: 10px;
          padding: 1px 5px;
          background: ${t.color.surfaceMuted};
          border: 1px solid ${t.color.border};
          border-radius: 4px;
          color: ${t.color.textMuted};
        }
        .askai-suggestion-card:hover {
          border-color: var(--accent) !important;
          background: var(--accent-soft) !important;
        }
        .askai-followup-chip:hover {
          background: #eff4fb !important;
          border-color: #d6e4ff !important;
        }
        .askai-history-row[data-current="false"]:hover {
          background: ${t.color.surfaceMuted} !important;
        }
        .askai-doc-row {
          transition: background 0.15s ease;
        }
        .askai-doc-row:hover {
          background: ${t.color.surfaceMuted};
        }
        .askai-suggestion-card:hover .askai-card-arrow {
          color: var(--accent) !important;
          transform: translateX(3px);
        }
        /* Tighten markdown bubble layout — react-markdown leaves stock <p>,
           <ul>, <table> margins that, combined with double-newlines from the
           LLM, produce visibly chunky gaps between paragraphs. */
        .askai-markdown > *:first-child { margin-top: 0 !important; }
        .askai-markdown > *:last-child  { margin-bottom: 0 !important; }
        .askai-markdown p + p { margin-top: 4px !important; }
        /* Render bullets manually via ::before so the marker is independent
           of the app's global list-style: none reset on ol/ul. */
        .askai-markdown ul, .askai-markdown ol {
          margin: 4px 0 !important;
          padding-left: 22px !important;
          list-style: none !important;
        }
        .askai-markdown li {
          margin-bottom: 2px !important;
          display: list-item !important;
          list-style: none !important;
          position: relative !important;
        }
        .askai-markdown ul > li::before {
          content: "•";
          position: absolute;
          left: -14px;
          top: 0;
          color: #475569;
          font-weight: 700;
          line-height: inherit;
        }
        .askai-markdown ol {
          counter-reset: askai-ol;
        }
        .askai-markdown ol > li {
          counter-increment: askai-ol;
        }
        .askai-markdown ol > li::before {
          content: counter(askai-ol) ".";
          position: absolute;
          left: -22px;
          top: 0;
          width: 18px;
          text-align: right;
          color: #475569;
        }
        /* GFM task list — drop the bullet, the checkbox is the marker. */
        .askai-markdown li.task-list-item::before {
          content: "" !important;
        }
        .askai-markdown h1, .askai-markdown h2, .askai-markdown h3,
        .askai-markdown h4, .askai-markdown h5 {
          margin-top: 8px !important;
          margin-bottom: 4px !important;
        }
        .askai-markdown table { margin: 6px 0 !important; }
      `}</style>

      <ChatHeader
        tenderTitle={tenderTitle}
        hasMessages={hasMessages}
        activePanel={sidePanel}
        onTogglePanel={togglePanel}
        onNewChat={handleNewChat}
        onExit={fullScreen ? onExit : undefined}
      />

      <div
        style={{
          flex: 1,
          display: "flex",
          minHeight: 0,
          // The mobile SidePanel renders as an absolute slide-over inside
          // this row, so we need a positioning context.
          position: "relative",
        }}
      >
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            position: "relative",
            background: t.color.surface,
          }}
        >
          <DropZoneOverlay visible={isDragging} />

          {sessionLoading || awaitingResumeDecision ? (
            <SessionLoadingState />
          ) : hasMessages ? (
            <MessageList
              messages={messages}
              thinkingLabel={displayThinkingLabel}
              copiedIdx={copiedIdx}
              feedback={feedback}
              canRegenerate={!isStreaming}
              onCopy={handleCopy}
              onFeedback={handleFeedback}
              onShare={handleShare}
              onRegenerate={handleRegenerate}
              onDownloadImage={handleDownloadImage}
              onSuggestedQuestion={(q) =>
                send({ text: q, attachments: [] })
              }
            />
          ) : (
            <EmptyState
              tenderTitle={tenderTitle}
              onPick={(q) => send({ text: q, attachments: [] })}
            />
          )}

          <ChatInput
            value={input}
            onChange={setInput}
            onSend={handleSend}
            onStop={handleStop}
            onPickFiles={addAttachments}
            onRemoveAttachment={removeAttachment}
            attachments={attachments}
            isStreaming={isStreaming}
            disabled={sessionLoading}
            centered={hasMessages}
            placeholder={
              sessionLoading
                ? "Restoring your last conversation…"
                : "Ask anything about this tender…"
            }
          />
        </div>

        <SidePanel
          open={sidePanel !== null}
          onClose={() => setSidePanel(null)}
        >
          {sidePanel === "documents" && (
            <DocumentsPanel
              docs={docs}
              loading={docsLoading}
              error={docsError}
              onRefresh={fetchDocs}
            />
          )}
          {sidePanel === "history" && (
            <HistoryPanel
              sessions={sessions}
              currentSessionId={sessionId}
              loading={sessionsLoading}
              error={sessionsError}
              onRefresh={fetchSessions}
              onSelect={handleSelectSession}
            />
          )}
        </SidePanel>
      </div>

      <ResumeModal
        open={awaitingResumeDecision}
        session={restoredSession}
        onResume={handleResume}
        onStartNew={handleStartNew}
      />

    </div>
    </DocViewerProvider>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Inline view-state helpers
// ─────────────────────────────────────────────────────────────────────────────

const SessionLoadingState: React.FC = () => (
  <div
    style={{
      flex: 1,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: t.space.s5,
      fontFamily: t.font.sans,
    }}
  >
    <div style={{ position: "relative", width: 58, height: 58 }}>
      {/* Soft breathing halo */}
      <motion.div
        aria-hidden
        animate={{
          scale: [1, 1.35, 1],
          opacity: [0.55, 0.1, 0.55],
        }}
        transition={{
          duration: 2.6,
          ease: "easeInOut",
          repeat: Infinity,
        }}
        style={{
          position: "absolute",
          inset: -10,
          borderRadius: "50%",
          background:
            "radial-gradient(closest-side, rgba(96, 165, 250, 0.55), rgba(96, 165, 250, 0) 72%)",
          pointerEvents: "none",
        }}
      />
      {/* Rotating dusk-gradient orb (mini brand mark) */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: "50%",
          overflow: "hidden",
          boxShadow:
            "0 0 0 1px rgba(15, 23, 42, 0.06), 0 12px 28px -12px rgba(34, 99, 179, 0.4)",
        }}
      >
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 14, ease: "linear", repeat: Infinity }}
          style={{
            position: "absolute",
            inset: -14,
            background:
              "linear-gradient(180deg, #0c1a3e 0%, #1e3a8a 22%, #2563eb 38%, #6dd5e8 56%, #f8c8b8 78%, #f1715d 100%)",
          }}
        />
      </div>
      {/* Center icon with a subtle scale breath */}
      <motion.div
        animate={{ scale: [1, 1.06, 1] }}
        transition={{
          duration: 2.6,
          ease: "easeInOut",
          repeat: Infinity,
        }}
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "rgba(255, 255, 255, 0.96)",
          fontSize: 22,
          textShadow: "0 1px 2px rgba(0, 0, 0, 0.2)",
          pointerEvents: "none",
        }}
      >
        <ThunderboltFilled />
      </motion.div>
    </div>
    {/* Shimmer-swept caption */}
    <motion.div
      animate={{ backgroundPosition: ["-150% 0", "150% 0"] }}
      transition={{ duration: 2.4, ease: "linear", repeat: Infinity }}
      style={{
        fontSize: 13,
        fontWeight: 500,
        letterSpacing: 0.2,
        backgroundImage:
          "linear-gradient(90deg, #94a3b8 0%, #94a3b8 30%, #2563eb 50%, #94a3b8 70%, #94a3b8 100%)",
        backgroundSize: "200% 100%",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
        color: "transparent",
      }}
    >
      Restoring your last conversation…
    </motion.div>
  </div>
);

interface ResumeModalProps {
  open: boolean;
  session: {
    sessionId: string;
    title?: string;
    messageCount: number;
    lastMessageAt?: number;
  } | null;
  onResume: () => void;
  onStartNew: () => void;
}

const ResumeModal: React.FC<ResumeModalProps> = ({
  open,
  session,
  onResume,
  onStartNew,
}) => {
  const lastSeen = session?.lastMessageAt
    ? formatDistanceToNow(new Date(session.lastMessageAt), { addSuffix: true })
    : null;
  return (
    <Modal
      open={open}
      closable={false}
      maskClosable={false}
      keyboard={false}
      footer={null}
      centered
      width={460}
      zIndex={1100}
      styles={{
        body: { padding: 0 },
        content: { padding: 0, borderRadius: 16, overflow: "hidden" },
      }}
    >
      {session && (
        <div style={{ padding: "26px 28px 22px", fontFamily: t.font.sans }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 44,
              height: 44,
              borderRadius: 12,
              background: t.color.accentSoft,
              color: t.color.accent,
              marginBottom: 16,
              fontSize: 20,
            }}
          >
            <MessageOutlined />
          </div>
          <div
            style={{
              fontSize: 19,
              fontWeight: 600,
              color: t.color.text,
              letterSpacing: -0.2,
              marginBottom: 6,
            }}
          >
            Pick up where you left off?
          </div>
          <div
            style={{
              fontSize: 13.5,
              color: t.color.textMuted,
              lineHeight: 1.55,
              marginBottom: 18,
            }}
          >
            You have a previous conversation about this tender —{" "}
            <strong style={{ color: t.color.text, fontWeight: 600 }}>
              {session.messageCount} message
              {session.messageCount === 1 ? "" : "s"}
            </strong>
            {lastSeen && <>, last reply {lastSeen}</>}. Resume it or start
            fresh.
          </div>
          <div
            style={{
              display: "flex",
              gap: 8,
              justifyContent: "flex-end",
            }}
          >
            <Button
              onClick={onStartNew}
              icon={<FormOutlined />}
              style={{
                borderRadius: 8,
                fontWeight: 500,
              }}
            >
              Start a new chat
            </Button>
            <Button
              type="primary"
              onClick={onResume}
              icon={<MessageOutlined />}
              style={{
                background: t.color.accent,
                borderColor: t.color.accent,
                borderRadius: 8,
                fontWeight: 500,
              }}
            >
              Resume conversation
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AskAIView;
